import React, { useState, useMemo } from 'react';
import { Fragrance, JournalEntry, Ingredient } from '../App';
import { IconX, IconChevron, IconHeart } from './Icons';

interface GlossaryProps {
  ingredients: Ingredient[];
  journalEntries: JournalEntry[];
  catalog: Fragrance[];
}

const CATEGORIES = ['All', 'Woods', 'Florals', 'Citrus', 'Spices', 'Resins & Orientals', 'Musks', 'Greens & Aquatics', 'Fruits', 'Tobacco & Gourmands'];

const noteToId = (note: string): string => note.toLowerCase().replace(/\s+/g, '-');

// Filter values match actual journal rating values
type RatingFilter = 'all' | 'love' | 'like' | 'neutral' | 'dislike';

const Glossary: React.FC<GlossaryProps> = ({ ingredients, journalEntries, catalog }) => {
  const [showFilter, setShowFilter] = useState<RatingFilter>('all');
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [fragrancePopup, setFragrancePopup] = useState<Fragrance | null>(null);

  const ingredientData = useMemo(() => {
    const map: Record<string, { fragrances: { name: string; id: string }[]; ratings: string[] }> = {};

    for (const entry of journalEntries) {
      const catalogFrag = catalog.find(f => f.fragranceId === entry.fragranceId);
      if (!catalogFrag) continue;

      const allNotes = [
        ...(catalogFrag.topNotes || []),
        ...(catalogFrag.middleNotes || []),
        ...(catalogFrag.baseNotes || []),
        ...(catalogFrag.notes || []),
      ];
      const uniqueNotes = Array.from(new Set(allNotes));

      for (const note of uniqueNotes) {
        const id = noteToId(note);
        if (!map[id]) map[id] = { fragrances: [], ratings: [] };
        if (!map[id].fragrances.find(f => f.id === catalogFrag.fragranceId)) {
          map[id].fragrances.push({ name: catalogFrag.name, id: catalogFrag.fragranceId });
          map[id].ratings.push(entry.rating);
        }
      }
    }

    return map;
  }, [journalEntries, catalog]);

  const counts = useMemo(() => {
    let love = 0, like = 0, neutral = 0, dislike = 0;
    for (const [id, data] of Object.entries(ingredientData)) {
      if (ingredients.find(i => i.ingredientId === id)) {
        if (data.ratings.some(r => r === 'love')) love++;
        if (data.ratings.some(r => r === 'like')) like++;
        if (data.ratings.some(r => r === 'neutral')) neutral++;
        if (data.ratings.some(r => r === 'dislike')) dislike++;
      }
    }
    return { love, like, neutral, dislike };
  }, [ingredientData, ingredients]);

  const filtered = ingredients.filter(ing => {
    const categoryMatch = activeCategory === 'All' || ing.category === activeCategory;
    const searchMatch = !search || ing.name.toLowerCase().includes(search.toLowerCase());
    if (showFilter === 'all') return categoryMatch && searchMatch;
    const data = ingredientData[ing.ingredientId];
    if (!data) return false;
    const ratingMatch = data.ratings.some(r => r === showFilter);
    return categoryMatch && searchMatch && ratingMatch;
  });

  if (ingredients.length === 0) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="text-stone">Loading ingredients...</p>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-cream px-6 py-10 pb-24 md:pb-10">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl text-charcoal mb-2">Ingredients</h2>
          <p className="text-stone-light text-sm mb-4">The building blocks of fine fragrance</p>

          {/* Search */}
          <div className="relative mb-6">
            <input
              type="text"
              value={search}
              onChange={e => { setSearch((e.target as HTMLInputElement).value); setExpanded(null); }}
              placeholder="Search ingredients..."
              className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 pr-10 text-charcoal placeholder-stone-light text-sm focus:outline-none focus:border-stone transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-light hover:text-charcoal transition-colors"
              >
                <IconX size={14} />
              </button>
            )}
          </div>

          {/* Filter by journal rating */}
          <div className="flex items-center gap-4 mb-6 border-b border-cream-dark pb-3 overflow-x-auto scrollbar-none -mx-6 px-6">
            {([
              { value: 'all' as RatingFilter, label: 'All', badge: undefined },
              { value: 'love' as RatingFilter, label: '★ All Time Fave', badge: counts.love },
              { value: 'like' as RatingFilter, label: 'Liked', badge: counts.like },
              { value: 'neutral' as RatingFilter, label: '~ Neutral', badge: counts.neutral },
              { value: 'dislike' as RatingFilter, label: '✕ Disliked', badge: counts.dislike },
            ] as const).map(opt => (
              <button
                key={opt.value}
                onClick={() => setShowFilter(opt.value)}
                className={`text-xs tracking-widest uppercase transition-colors pb-3 -mb-3 border-b-2 flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 ${
                  showFilter === opt.value ? 'text-charcoal border-charcoal' : 'text-stone-light border-transparent hover:text-stone'
                }`}
              >
                {opt.value === 'like' && <IconHeart size={11} className="flex-shrink-0" />}
                {opt.label}
                {opt.badge !== undefined && opt.badge > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${showFilter === opt.value ? 'bg-charcoal text-cream' : 'bg-cream-dark text-stone'}`}>
                    {opt.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Category filters */}
          <div className="flex gap-2 flex-wrap mb-6">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1 text-xs tracking-widest uppercase border rounded-xl transition-colors ${
                  activeCategory === cat
                    ? 'bg-charcoal text-cream border-charcoal'
                    : 'bg-white text-stone border-cream-dark hover:border-stone'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <p className="text-stone-light text-xs tracking-widest uppercase mb-4">
            {filtered.length} ingredient{filtered.length !== 1 ? 's' : ''}
            {showFilter !== 'all' && journalEntries.length === 0 && (
              <span className="ml-2 normal-case text-stone">— add fragrances to your journal to populate this</span>
            )}
          </p>

          <div className="space-y-2">
            {filtered.map(ingredient => {
              const data = ingredientData[ingredient.ingredientId];
              const fragrancesWithThis = data?.fragrances || [];
              const isInCollection = fragrancesWithThis.length > 0;

              return (
                <div key={ingredient.ingredientId} className="bg-white border border-cream-dark rounded-xl">
                  <div
                    className="w-full p-5 text-left flex justify-between items-center cursor-pointer hover:bg-cream transition-colors"
                    onClick={() => setExpanded(expanded === ingredient.ingredientId ? null : ingredient.ingredientId)}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <div>
                        <p className="font-serif text-lg text-charcoal">{ingredient.name}</p>
                        <p className="text-xs tracking-widest uppercase text-stone-light">{ingredient.category}</p>
                      </div>
                      {isInCollection && (
                        <span className="text-xs tracking-widest uppercase text-stone bg-cream-dark px-2 py-1">
                          ✓ My collection
                        </span>
                      )}
                    </div>
                    <div className="ml-4 flex-shrink-0 w-7 h-7 rounded-full border border-cream-dark bg-white flex items-center justify-center">
                      <IconChevron size={11} direction={expanded === ingredient.ingredientId ? 'up' : 'down'} className="text-stone-light" />
                    </div>
                  </div>

                  {expanded === ingredient.ingredientId && (
                    <div className="px-5 pb-5 border-t border-cream-dark pt-4">
                      <p className="text-stone text-sm leading-relaxed">{ingredient.description}</p>
                      {fragrancesWithThis.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs tracking-widest uppercase text-stone-light mb-2">In your journal</p>
                          <div className="flex flex-wrap gap-2">
                            {fragrancesWithThis.map((frag, i) => {
                              const catalogFrag = catalog.find(f => f.fragranceId === frag.id);
                              return (
                                <button
                                  key={i}
                                  onClick={() => catalogFrag && setFragrancePopup(catalogFrag)}
                                  className={`text-xs bg-cream-dark text-stone px-3 py-1 rounded-lg ${catalogFrag ? 'hover:bg-stone hover:text-cream transition-colors' : 'cursor-default'}`}
                                >
                                  {frag.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Fragrance info popup */}
      {fragrancePopup && (
        <>
          <div className="fixed inset-0 bg-charcoal bg-opacity-40 z-40" onClick={() => setFragrancePopup(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cream z-50 w-96 rounded-xl max-w-[92vw] p-6 shadow-lg max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="font-serif text-2xl text-charcoal">{fragrancePopup.name}</p>
                <p className="text-stone-light text-sm">{fragrancePopup.house}{fragrancePopup.year ? ` · ${fragrancePopup.year}` : ''}</p>
              </div>
              <button onClick={() => setFragrancePopup(null)} className="text-stone-light hover:text-charcoal ml-4 flex-shrink-0"><IconX size={16} /></button>
            </div>
            {fragrancePopup.description && (
              <p className="text-stone text-sm leading-relaxed mb-4">{fragrancePopup.description}</p>
            )}
            {fragrancePopup.topNotes && fragrancePopup.topNotes.length > 0 && (
              <div className="mb-3">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Top notes</p>
                <div className="flex flex-wrap gap-2">
                  {fragrancePopup.topNotes.map((n, i) => <span key={i} className="text-xs bg-cream-dark text-stone px-3 py-1">{n}</span>)}
                </div>
              </div>
            )}
            {fragrancePopup.middleNotes && fragrancePopup.middleNotes.length > 0 && (
              <div className="mb-3">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Heart notes</p>
                <div className="flex flex-wrap gap-2">
                  {fragrancePopup.middleNotes.map((n, i) => <span key={i} className="text-xs bg-cream-dark text-stone px-3 py-1">{n}</span>)}
                </div>
              </div>
            )}
            {fragrancePopup.baseNotes && fragrancePopup.baseNotes.length > 0 && (
              <div className="mb-3">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Base notes</p>
                <div className="flex flex-wrap gap-2">
                  {fragrancePopup.baseNotes.map((n, i) => <span key={i} className="text-xs bg-cream-dark text-stone px-3 py-1">{n}</span>)}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Glossary;
